import type * as Core from '../../types/calculation-core.js';
import type { HomeRenovationToolId } from '../../types/tool-registry.js';
import type { NumberRule, ExactDecimal } from './contracts.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
const GEOMETRY_SOURCE = {
    id: 'openstax-geometry-applications',
    title: 'Prealgebra 2e — Solve Geometry Applications: Circles and Irregular Figures',
    publisher: 'OpenStax',
    url: 'https://openstax.org/books/prealgebra-2e/pages/9-5-solve-geometry-applications-circles-and-irregular-figures',
    verifiedAt: '2026-08-20',
};
function staticMetadata(toolId: HomeRenovationToolId): Core.CoreMetadata {
    const isCurtainFabric = toolId === 'curtain-fabric';
    return {
        calculatorId: toolId,
        version: isCurtainFabric ? '2.0.0' : '1.0.0',
        effectiveFrom: isCurtainFabric ? '2026-08-22' : '2026-08-20',
        lastVerifiedAt: isCurtainFabric ? '2026-08-22' : '2026-08-20',
        sources: [
            GEOMETRY_SOURCE,
            {
                id: `hephesapla-${toolId}-method`,
                title: `HepHesapla — ${toolId} statik hesaplama yöntemi`,
                publisher: 'HepHesapla',
                url: isCurtainFabric
                    ? 'https://hephesapla.com/ev-tadilat/perde-kumasi-metresi-hesaplama'
                    : `https://hephesapla.com/ev-tadilat/${toolId}-hesaplama`,
                verifiedAt: isCurtainFabric ? '2026-08-22' : '2026-08-20',
            },
        ],
        notes: [
            'Hesap, geometrik ölçüler ile kullanıcının girdiği ürün değerlerini işleyen statik bir yöntemdir; dönemsel mevzuat veya tarife içermez.',
            'Tüketim, verim, yoğunluk, paket ölçüsü ve fiyat gibi ürüne özgü değerler kullanıcı girdisidir; çekirdeğe güncel ürün verisi gömülmez.',
        ],
    };
}
function defaultConfig(toolId: HomeRenovationToolId): Core.BaseConfig {
    return { metadata: staticMetadata(toolId), maxAbsoluteInput: 1e9 };
}
export const DEFAULT_HOME_RENOVATION_CONFIGS = {
    'alan-fire': defaultConfig('alan-fire'),
    'alci-siva-macun': defaultConfig('alci-siva-macun'),
    boya: defaultConfig('boya'),
    'derz-dolgu': defaultConfig('derz-dolgu'),
    'duvar-kagidi-rulo': defaultConfig('duvar-kagidi-rulo'),
    'fayans-seramik': defaultConfig('fayans-seramik'),
    'curtain-fabric': defaultConfig('curtain-fabric'),
    'hali-metrekare': defaultConfig('hali-metrekare'),
    'kutu-paket-sayisi': defaultConfig('kutu-paket-sayisi'),
    'laminat-parke': defaultConfig('laminat-parke'),
    'oda-metrekare': defaultConfig('oda-metrekare'),
    'supurgelik-metre': defaultConfig('supurgelik-metre'),
    'zebra-stor-perde-olcu': defaultConfig('zebra-stor-perde-olcu'),
};
const TOOL_IDS = Object.freeze(Object.keys(DEFAULT_HOME_RENOVATION_CONFIGS));
const MIN_STABLE_POSITIVE = 2 ** -1022;
function isToolId(value: unknown): value is HomeRenovationToolId {
    return typeof value === 'string' && TOOL_IDS.includes(value);
}
function validateConfig(config: Core.BaseConfig, toolId: HomeRenovationToolId): Core.InputError[] {
    const errors = [...validateMetadata(config.metadata)];
    if (!isFiniteNumber(config.maxAbsoluteInput) || config.maxAbsoluteInput <= 0) {
        errors.push({
            field: 'config.maxAbsoluteInput',
            code: 'INVALID_CONFIG',
            message: 'Azami giriş sınırı pozitif ve sonlu olmalıdır.',
        });
    }
    if (config.metadata
        && typeof config.metadata === 'object'
        && typeof config.metadata.calculatorId === 'string'
        && config.metadata.calculatorId !== toolId) {
        errors.push({
            field: 'config.metadata.calculatorId',
            code: 'INVALID_CONFIG',
            message: 'Konfigürasyon hesap kimliği araç kimliğiyle eşleşmelidir.',
        });
    }
    return errors;
}
function readNumber(input: Readonly<Record<string, unknown>>, rule: NumberRule, config: Core.BaseConfig, errors: Core.InputError[]) {
    const raw = input[rule.field];
    const value = raw === undefined ? rule.defaultValue : raw;
    if (value === undefined) {
        errors.push({ field: rule.field, code: 'REQUIRED', message: `${rule.field} alanı gereklidir.` });
        return Number.NaN;
    }
    if (typeof value !== 'number' || !isFiniteNumber(value)) {
        errors.push({ field: rule.field, code: 'NOT_FINITE', message: `${rule.field} sonlu bir sayı olmalıdır.` });
        return Number.NaN;
    }
    if (Math.abs(value) > config.maxAbsoluteInput) {
        errors.push({ field: rule.field, code: 'OUT_OF_RANGE', message: `${rule.field} izin verilen aralığın dışındadır.` });
    }
    if (value !== 0 && Math.abs(value) < MIN_STABLE_POSITIVE) {
        errors.push({
            field: rule.field,
            code: 'OUT_OF_RANGE',
            message: `${rule.field} güvenilir hesaplama için temsil edilebilir normal sayı aralığında olmalıdır.`,
        });
    }
    if (rule.range === 'positive' ? value <= 0 : value < 0) {
        errors.push({
            field: rule.field,
            code: 'OUT_OF_RANGE',
            message: `${rule.field} ${rule.range === 'positive' ? 'sıfırdan büyük' : 'sıfır veya daha büyük'} olmalıdır.`,
        });
    }
    if (rule.integer === true && !Number.isInteger(value)) {
        errors.push({ field: rule.field, code: 'OUT_OF_RANGE', message: `${rule.field} tam sayı olmalıdır.` });
    }
    return value;
}
function readBoolean(input: Readonly<Record<string, unknown>>, field: string, defaultValue: boolean, errors: Core.InputError[]) {
    const value = input[field] === undefined ? defaultValue : input[field];
    if (typeof value !== 'boolean') {
        errors.push({ field, code: 'UNSUPPORTED_VALUE', message: `${field} doğru/yanlış değeri olmalıdır.` });
        return defaultValue;
    }
    return value;
}
function readEnum<T extends string | number>(input: Readonly<Record<string, unknown>>, field: string, allowed: readonly T[], defaultValue: T, errors: Core.InputError[]) {
    const value = input[field] === undefined ? defaultValue : input[field];
    const accepted = allowed.find(choice => choice === value);
    if (accepted === undefined) {
        errors.push({ field, code: 'UNSUPPORTED_VALUE', message: `${field} desteklenen değerlerden biri olmalıdır.` });
        return defaultValue;
    }
    return accepted;
}
function estimatedCost(quantity: number, price: number) {
    return price > 0 ? quantity * price : null;
}
function exactDecimal(value: number): ExactDecimal {
    const match = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/.exec(value.toString());
    if (!match || match[2] === undefined) {
        throw new Error('Sonlu sayı exact ondalık biçime çevrilemedi.');
    }
    const fraction = match[3] ?? '';
    return {
        coefficient: BigInt(`${match[1] ?? ''}${match[2]}${fraction}`),
        exponent: Number(match[4] ?? 0) - fraction.length,
    };
}
function decimalPowerOfTen(exponent: number) {
    return 10n ** BigInt(exponent);
}
function addExactDecimals(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
    const exponent = Math.min(left.exponent, right.exponent);
    return {
        coefficient: left.coefficient * decimalPowerOfTen(left.exponent - exponent)
            + right.coefficient * decimalPowerOfTen(right.exponent - exponent),
        exponent,
    };
}
function multiplyExactDecimals(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
    return {
        coefficient: left.coefficient * right.coefficient,
        exponent: left.exponent + right.exponent,
    };
}
function scaleExactDecimal(value: ExactDecimal, coefficient: bigint, exponentDelta: number = 0): ExactDecimal {
    return {
        coefficient: value.coefficient * coefficient,
        exponent: value.exponent + exponentDelta,
    };
}
function subtractExactDecimals(left: ExactDecimal, right: ExactDecimal): ExactDecimal {
    return addExactDecimals(left, scaleExactDecimal(right, -1n));
}
function exactDecimalSign(value: ExactDecimal) {
    if (value.coefficient < 0n)
        return -1;
    if (value.coefficient > 0n)
        return 1;
    return 0;
}
function exactDecimalToNumber(value: ExactDecimal) {
    if (value.coefficient === 0n)
        return 0;
    return Number(`${value.coefficient.toString()}e${value.exponent}`);
}
function exactPositiveRatio(numerator: ExactDecimal, denominator: ExactDecimal) {
    if (numerator.coefficient <= 0n || denominator.coefficient <= 0n) {
        throw new Error('Exact oran yalnız pozitif değerlerle kurulabilir.');
    }
    const exponentDelta = numerator.exponent - denominator.exponent;
    return exponentDelta >= 0
        ? {
            numerator: numerator.coefficient * decimalPowerOfTen(exponentDelta),
            denominator: denominator.coefficient,
        }
        : {
            numerator: numerator.coefficient,
            denominator: denominator.coefficient * decimalPowerOfTen(-exponentDelta),
        };
}
function ceilExactPositiveRatio(numerator: ExactDecimal, denominator: ExactDecimal) {
    const ratio = exactPositiveRatio(numerator, denominator);
    return (ratio.numerator + ratio.denominator - 1n) / ratio.denominator;
}
function exactQuantityWithWaste(quantity: ExactDecimal, wastePercent: number) {
    const factor = addExactDecimals(exactDecimal(1), scaleExactDecimal(exactDecimal(wastePercent), 1n, -2));
    return multiplyExactDecimals(quantity, factor);
}
function exactPurchaseCount(quantity: ExactDecimal, packageSize: ExactDecimal) {
    return quantity.coefficient === 0n ? 0 : exactDecimalToNumber({ coefficient: ceilExactPositiveRatio(quantity, packageSize), exponent: 0 });
}
function floorExactPositiveRatio(numerator: ExactDecimal, denominator: ExactDecimal) {
    const ratio = exactPositiveRatio(numerator, denominator);
    return ratio.numerator / ratio.denominator;
}
function exactPaintGrossMinusOpenings(roomWidthM: number, roomLengthM: number, wallHeightM: number, openingsAreaM2: number, includeCeiling: boolean) {
    const width = exactDecimal(roomWidthM);
    const length = exactDecimal(roomLengthM);
    const wallArea = scaleExactDecimal(multiplyExactDecimals(addExactDecimals(width, length), exactDecimal(wallHeightM)), 2n);
    const ceilingArea = includeCeiling
        ? multiplyExactDecimals(width, length)
        : exactDecimal(0);
    return subtractExactDecimals(addExactDecimals(wallArea, ceilingArea), exactDecimal(openingsAreaM2));
}
function exactSkirtingPerimeterMinusDoors(roomWidthM: number, roomLengthM: number, doorCount: number, doorWidthCm: number) {
    const perimeter = scaleExactDecimal(addExactDecimals(exactDecimal(roomWidthM), exactDecimal(roomLengthM)), 2n);
    const doorOpening = scaleExactDecimal(exactDecimal(doorWidthCm), BigInt(doorCount), -2);
    return subtractExactDecimals(perimeter, doorOpening);
}
function finiteResult<T>(value: T, numbers: readonly (number | null)[], metadata: Core.CoreMetadata, requiredPositiveNumbers: readonly (number | null)[] = []) {
    const hasOnlyFiniteNumbers = numbers.every((number) => number === null || Number.isFinite(number));
    const hasOnlyStablePositiveNumbers = requiredPositiveNumbers.every((number) => number === null || (Number.isFinite(number) && number >= MIN_STABLE_POSITIVE));
    return hasOnlyFiniteNumbers && hasOnlyStablePositiveNumbers
        ? success(value, metadata)
        : failure([{
                field: 'result',
                code: 'CALCULATION_OVERFLOW',
                message: 'Sonuç temsil edilebilir sayı aralığını aştı veya pozitif büyüklük sıfıra yuvarlandı.',
            }], metadata);
}
function calculateAreaFire(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const netAmount = readNumber(input, { field: 'netAmount', range: 'positive' }, config, errors);
    const wastePercent = readNumber(input, { field: 'wastePercent', defaultValue: 10, range: 'nonnegative' }, config, errors);
    const unitPrice = readNumber(input, { field: 'unitPrice', defaultValue: 0, range: 'nonnegative' }, config, errors);
    const unit = readEnum(input, 'unit', ['m²', 'metre', 'adet', 'kg', 'litre'], 'm²', errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const wasteAmount = (netAmount * wastePercent) / 100;
    const grossAmount = netAmount + wasteAmount;
    const cost = estimatedCost(grossAmount, unitPrice);
    return finiteResult({ toolId: 'alan-fire', unit, netAmount, wastePercent, wasteAmount, grossAmount, unitPrice, estimatedCost: cost }, [wasteAmount, grossAmount, cost], config.metadata, [...(wastePercent > 0 ? [wasteAmount] : []), grossAmount, cost]);
}
function calculatePlaster(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const areaM2 = readNumber(input, { field: 'areaM2', range: 'positive' }, config, errors);
    const thicknessMm = readNumber(input, { field: 'thicknessMm', range: 'positive' }, config, errors);
    const consumptionKgPerM2Mm = readNumber(input, { field: 'consumptionKgPerM2Mm', range: 'positive' }, config, errors);
    const wastePercent = readNumber(input, { field: 'wastePercent', defaultValue: 10, range: 'nonnegative' }, config, errors);
    const bagKg = readNumber(input, { field: 'bagKg', defaultValue: 25, range: 'positive' }, config, errors);
    const bagPrice = readNumber(input, { field: 'bagPrice', defaultValue: 0, range: 'nonnegative' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const netKg = areaM2 * thicknessMm * consumptionKgPerM2Mm;
    const grossKg = netKg * (1 + wastePercent / 100);
    const exactNetKg = multiplyExactDecimals(multiplyExactDecimals(exactDecimal(areaM2), exactDecimal(thicknessMm)), exactDecimal(consumptionKgPerM2Mm));
    const bagCount = exactPurchaseCount(exactQuantityWithWaste(exactNetKg, wastePercent), exactDecimal(bagKg));
    const purchasedKg = bagCount * bagKg;
    const cost = estimatedCost(bagCount, bagPrice);
    return finiteResult({
        toolId: 'alci-siva-macun', areaM2, thicknessMm, consumptionKgPerM2Mm, wastePercent,
        netKg, grossKg, bagKg, bagCount, purchasedKg, bagPrice, estimatedCost: cost,
    }, [netKg, grossKg, bagCount, purchasedKg, cost], config.metadata, [netKg, grossKg, bagCount, purchasedKg, cost]);
}
function calculatePaint(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const roomWidthM = readNumber(input, { field: 'roomWidthM', range: 'positive' }, config, errors);
    const roomLengthM = readNumber(input, { field: 'roomLengthM', range: 'positive' }, config, errors);
    const wallHeightM = readNumber(input, { field: 'wallHeightM', range: 'positive' }, config, errors);
    const openingsAreaM2 = readNumber(input, { field: 'openingsAreaM2', defaultValue: 0, range: 'nonnegative' }, config, errors);
    const coatCount = readNumber(input, { field: 'coatCount', defaultValue: 2, range: 'positive', integer: true }, config, errors);
    const coverageM2PerLitre = readNumber(input, { field: 'coverageM2PerLitre', defaultValue: 10, range: 'positive' }, config, errors);
    const wastePercent = readNumber(input, { field: 'wastePercent', defaultValue: 10, range: 'nonnegative' }, config, errors);
    const bucketLitres = readNumber(input, { field: 'bucketLitres', defaultValue: 15, range: 'positive' }, config, errors);
    const litrePrice = readNumber(input, { field: 'litrePrice', defaultValue: 0, range: 'nonnegative' }, config, errors);
    const includeCeiling = readBoolean(input, 'includeCeiling', false, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const wallAreaM2 = 2 * (roomWidthM + roomLengthM) * wallHeightM;
    const ceilingAreaM2 = includeCeiling ? roomWidthM * roomLengthM : 0;
    const exactNetPaintAreaM2 = exactPaintGrossMinusOpenings(roomWidthM, roomLengthM, wallHeightM, openingsAreaM2, includeCeiling);
    const paintAreaOrder = exactDecimalSign(exactNetPaintAreaM2);
    if (paintAreaOrder < 0) {
        return failure([{
                field: 'openingsAreaM2',
                code: 'OUT_OF_RANGE',
                message: 'Kapı/pencere alanı toplam duvar ve tavan alanını aşamaz.',
            }], config.metadata);
    }
    const netPaintAreaM2 = exactDecimalToNumber(exactNetPaintAreaM2);
    const theoreticalLitres = (netPaintAreaM2 * coatCount) / coverageM2PerLitre;
    const grossLitres = theoreticalLitres * (1 + wastePercent / 100);
    const exactCoatedAreaM2 = multiplyExactDecimals(exactNetPaintAreaM2, exactDecimal(coatCount));
    const exactBucketCoverageM2 = multiplyExactDecimals(exactDecimal(coverageM2PerLitre), exactDecimal(bucketLitres));
    const bucketCount = exactPurchaseCount(exactQuantityWithWaste(exactCoatedAreaM2, wastePercent), exactBucketCoverageM2);
    const purchasedLitres = bucketCount * bucketLitres;
    const cost = estimatedCost(purchasedLitres, litrePrice);
    return finiteResult({
        toolId: 'boya', wallAreaM2, ceilingAreaM2, netPaintAreaM2, coatCount, coverageM2PerLitre,
        wastePercent, theoreticalLitres, grossLitres, bucketLitres, bucketCount, purchasedLitres,
        litrePrice, estimatedCost: cost,
    }, [wallAreaM2, ceilingAreaM2, netPaintAreaM2, theoreticalLitres, grossLitres, bucketCount, purchasedLitres, cost], config.metadata, [
        wallAreaM2,
        ...(includeCeiling ? [ceilingAreaM2] : []),
        ...(paintAreaOrder > 0
            ? [netPaintAreaM2, theoreticalLitres, grossLitres, bucketCount, purchasedLitres, cost]
            : []),
    ]);
}
function calculateGrout(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const areaM2 = readNumber(input, { field: 'areaM2', range: 'positive' }, config, errors);
    const tileWidthCm = readNumber(input, { field: 'tileWidthCm', range: 'positive' }, config, errors);
    const tileHeightCm = readNumber(input, { field: 'tileHeightCm', range: 'positive' }, config, errors);
    const jointWidthMm = readNumber(input, { field: 'jointWidthMm', range: 'positive' }, config, errors);
    const jointDepthMm = readNumber(input, { field: 'jointDepthMm', range: 'positive' }, config, errors);
    const densityCoefficient = readNumber(input, { field: 'densityCoefficient', defaultValue: 1.6, range: 'positive' }, config, errors);
    const wastePercent = readNumber(input, { field: 'wastePercent', defaultValue: 10, range: 'nonnegative' }, config, errors);
    const bagKg = readNumber(input, { field: 'bagKg', defaultValue: 5, range: 'positive' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const tileWidthMm = tileWidthCm * 10;
    const tileHeightMm = tileHeightCm * 10;
    const consumptionKgPerM2 = ((tileWidthMm + tileHeightMm) / (tileWidthMm * tileHeightMm))
        * jointWidthMm * jointDepthMm * densityCoefficient;
    const netKg = areaM2 * consumptionKgPerM2;
    const grossKg = netKg * (1 + wastePercent / 100);
    const exactTileWidthMm = scaleExactDecimal(exactDecimal(tileWidthCm), 10n);
    const exactTileHeightMm = scaleExactDecimal(exactDecimal(tileHeightCm), 10n);
    const exactJointVolume = [areaM2, jointWidthMm, jointDepthMm, densityCoefficient].reduce((quantity, value) => multiplyExactDecimals(quantity, exactDecimal(value)), addExactDecimals(exactTileWidthMm, exactTileHeightMm));
    const exactBagCoverage = multiplyExactDecimals(multiplyExactDecimals(exactTileWidthMm, exactTileHeightMm), exactDecimal(bagKg));
    const bagCount = exactPurchaseCount(exactQuantityWithWaste(exactJointVolume, wastePercent), exactBagCoverage);
    const purchasedKg = bagCount * bagKg;
    return finiteResult({ toolId: 'derz-dolgu', areaM2, consumptionKgPerM2, netKg, grossKg, wastePercent, bagKg, bagCount, purchasedKg }, [consumptionKgPerM2, netKg, grossKg, bagCount, purchasedKg], config.metadata, [consumptionKgPerM2, netKg, grossKg, bagCount, purchasedKg]);
}
function calculateWallpaper(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const wallWidthM = readNumber(input, { field: 'wallWidthM', range: 'positive' }, config, errors);
    const wallHeightM = readNumber(input, { field: 'wallHeightM', range: 'positive' }, config, errors);
    const rollWidthCm = readNumber(input, { field: 'rollWidthCm', range: 'positive' }, config, errors);
    const rollLengthM = readNumber(input, { field: 'rollLengthM', range: 'positive' }, config, errors);
    const patternRepeatCm = readNumber(input, { field: 'patternRepeatCm', defaultValue: 0, range: 'nonnegative' }, config, errors);
    const wastePercent = readNumber(input, { field: 'wastePercent', defaultValue: 5, range: 'nonnegative' }, config, errors);
    const rollPrice = readNumber(input, { field: 'rollPrice', defaultValue: 0, range: 'nonnegative' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const exactRollWidthM = scaleExactDecimal(exactDecimal(rollWidthCm), 1n, -2);
    const exactStripCount = ceilExactPositiveRatio(exactDecimal(wallWidthM), exactRollWidthM);
    const stripCount = exactDecimalToNumber({ coefficient: exactStripCount, exponent: 0 });
    const exactBaseStripLengthM = addExactDecimals(exactDecimal(wallHeightM), exactDecimal(0.1));
    const baseStripLengthM = exactDecimalToNumber(exactBaseStripLengthM);
    const exactPatternRepeatM = scaleExactDecimal(exactDecimal(patternRepeatCm), 1n, -2);
    const exactStripLengthM = patternRepeatCm > 0
        ? scaleExactDecimal(exactPatternRepeatM, ceilExactPositiveRatio(exactBaseStripLengthM, exactPatternRepeatM))
        : exactBaseStripLengthM;
    const stripLengthM = exactDecimalToNumber(exactStripLengthM);
    const exactStripsPerRoll = floorExactPositiveRatio(exactDecimal(rollLengthM), exactStripLengthM);
    if (exactStripsPerRoll < 1n) {
        return failure([{
                field: 'rollLengthM',
                code: 'OUT_OF_RANGE',
                message: 'Rulo boyu tek bir tam şeridi karşılayacak kadar uzun olmalıdır.',
            }], config.metadata);
    }
    const stripsPerRoll = exactDecimalToNumber({ coefficient: exactStripsPerRoll, exponent: 0 });
    const exactTotalStripLengthM = scaleExactDecimal(exactStripLengthM, exactStripCount);
    const totalStripLengthM = exactDecimalToNumber(exactTotalStripLengthM);
    const exactWasteFactor = addExactDecimals(exactDecimal(1), scaleExactDecimal(exactDecimal(wastePercent), 1n, -2));
    const exactGrossStripCount = multiplyExactDecimals({ coefficient: exactStripCount, exponent: 0 }, exactWasteFactor);
    const grossStripCount = exactDecimalToNumber(exactGrossStripCount);
    const exactRollCount = ceilExactPositiveRatio(exactGrossStripCount, { coefficient: exactStripsPerRoll, exponent: 0 });
    const rollCount = exactDecimalToNumber({ coefficient: exactRollCount, exponent: 0 });
    const purchasedLengthM = exactDecimalToNumber(scaleExactDecimal(exactDecimal(rollLengthM), exactRollCount));
    const cost = estimatedCost(rollCount, rollPrice);
    return finiteResult({
        toolId: 'duvar-kagidi-rulo', stripCount, baseStripLengthM, stripLengthM, totalStripLengthM,
        wastePercent, rollCount, rollLengthM, purchasedLengthM, rollPrice, estimatedCost: cost,
    }, [
        stripCount, baseStripLengthM, stripLengthM, stripsPerRoll, totalStripLengthM,
        grossStripCount, rollCount, purchasedLengthM, cost,
    ], config.metadata, [
        stripCount, baseStripLengthM, stripLengthM, stripsPerRoll, totalStripLengthM,
        grossStripCount, rollCount, purchasedLengthM, cost,
    ]);
}
function calculateTile(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const areaWidthM = readNumber(input, { field: 'areaWidthM', range: 'positive' }, config, errors);
    const areaLengthM = readNumber(input, { field: 'areaLengthM', range: 'positive' }, config, errors);
    const tileWidthCm = readNumber(input, { field: 'tileWidthCm', range: 'positive' }, config, errors);
    const tileHeightCm = readNumber(input, { field: 'tileHeightCm', range: 'positive' }, config, errors);
    const tilesPerBox = readNumber(input, { field: 'tilesPerBox', range: 'positive', integer: true }, config, errors);
    const wastePercent = readNumber(input, { field: 'wastePercent', defaultValue: 10, range: 'nonnegative' }, config, errors);
    const boxPrice = readNumber(input, { field: 'boxPrice', defaultValue: 0, range: 'nonnegative' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const areaM2 = areaWidthM * areaLengthM;
    const tileAreaM2 = (tileWidthCm / 100) * (tileHeightCm / 100);
    const exactAreaM2 = multiplyExactDecimals(exactDecimal(areaWidthM), exactDecimal(areaLengthM));
    const exactTileAreaM2 = scaleExactDecimal(multiplyExactDecimals(exactDecimal(tileWidthCm), exactDecimal(tileHeightCm)), 1n, -4);
    const neededTileCount = exactPurchaseCount(exactQuantityWithWaste(exactAreaM2, wastePercent), exactTileAreaM2);
    const boxCount = Math.ceil(neededTileCount / tilesPerBox);
    const purchasedAreaM2 = boxCount * tilesPerBox * tileAreaM2;
    const cost = estimatedCost(boxCount, boxPrice);
    return finiteResult({
        toolId: 'fayans-seramik', areaM2, tileAreaM2, wastePercent, neededTileCount, tilesPerBox,
        boxCount, purchasedAreaM2, boxPrice, estimatedCost: cost,
    }, [areaM2, tileAreaM2, neededTileCount, boxCount, purchasedAreaM2, cost], config.metadata, [areaM2, tileAreaM2, neededTileCount, boxCount, purchasedAreaM2, cost]);
}
function calculateCurtainFabric(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    if (input.mode === undefined) {
        errors.push({ field: 'mode', code: 'REQUIRED', message: 'Perde türü seçilmelidir.' });
    }
    const mode = readEnum(input, 'mode', ['tul', 'fon'], 'tul', errors);
    const curtainHeightCm = readNumber(input, { field: 'curtainHeightCm', range: 'positive' }, config, errors);
    const metrePrice = readNumber(input, { field: 'metrePrice', defaultValue: 0, range: 'nonnegative' }, config, errors);
    if (mode === 'tul') {
        const windowWidthCm = readNumber(input, { field: 'windowWidthCm', range: 'positive' }, config, errors);
        const fullness = readEnum(input, 'tulleFullness', [1, 2, 2.5, 3], 2.5, errors);
        const sideAllowanceCm = readNumber(input, { field: 'sideAllowanceCm', defaultValue: 20, range: 'nonnegative' }, config, errors);
        const hemAllowanceCm = readNumber(input, { field: 'tulleHemAllowanceCm', defaultValue: 20, range: 'nonnegative' }, config, errors);
        if (errors.length > 0)
            return failure(errors, config.metadata);
        const fabricLengthM = (windowWidthCm * fullness + sideAllowanceCm) / 100;
        const cutHeightM = (curtainHeightCm + hemAllowanceCm) / 100;
        const fabricAreaM2 = fabricLengthM * cutHeightM;
        const cost = estimatedCost(fabricLengthM, metrePrice);
        return finiteResult({
            toolId: 'curtain-fabric', mode, panelCount: null, finishedWidthCm: windowWidthCm,
            fullness, fabricLengthM, cutHeightM, fabricAreaM2, metrePrice, estimatedCost: cost,
        }, [windowWidthCm, fabricLengthM, cutHeightM, fabricAreaM2, cost], config.metadata, [windowWidthCm, fabricLengthM, cutHeightM, fabricAreaM2, cost]);
    }
    const panelCount = readNumber(input, { field: 'panelCount', defaultValue: 2, range: 'positive', integer: true }, config, errors);
    const finishedPanelWidthCm = readNumber(input, { field: 'finishedPanelWidthCm', range: 'positive' }, config, errors);
    const fullness = readEnum(input, 'draperyFullness', [1, 1.5, 2], 2, errors);
    const hemAllowanceCm = readNumber(input, { field: 'draperyHemAllowanceCm', defaultValue: 25, range: 'nonnegative' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const finishedWidthCm = finishedPanelWidthCm * panelCount;
    const fabricLengthM = (finishedWidthCm * fullness) / 100;
    const cutHeightM = (curtainHeightCm + hemAllowanceCm) / 100;
    const fabricAreaM2 = fabricLengthM * cutHeightM;
    const cost = estimatedCost(fabricLengthM, metrePrice);
    return finiteResult({
        toolId: 'curtain-fabric', mode, panelCount, finishedWidthCm, fullness, fabricLengthM,
        cutHeightM, fabricAreaM2, metrePrice, estimatedCost: cost,
    }, [panelCount, finishedWidthCm, fabricLengthM, cutHeightM, fabricAreaM2, cost], config.metadata, [panelCount, finishedWidthCm, fabricLengthM, cutHeightM, fabricAreaM2, cost]);
}
function calculateCarpet(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const widthCm = readNumber(input, { field: 'widthCm', range: 'positive' }, config, errors);
    const lengthCm = readNumber(input, { field: 'lengthCm', range: 'positive' }, config, errors);
    const pricePerM2 = readNumber(input, { field: 'pricePerM2', defaultValue: 0, range: 'nonnegative' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const areaM2 = (widthCm * lengthCm) / 10000;
    const perimeterM = (2 * (widthCm + lengthCm)) / 100;
    const cost = estimatedCost(areaM2, pricePerM2);
    return finiteResult({ toolId: 'hali-metrekare', areaM2, perimeterM, pricePerM2, estimatedCost: cost }, [areaM2, perimeterM, cost], config.metadata, [areaM2, perimeterM, cost]);
}
function calculatePackageCount(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const netNeedM2 = readNumber(input, { field: 'netNeedM2', range: 'positive' }, config, errors);
    const packageAreaM2 = readNumber(input, { field: 'packageAreaM2', range: 'positive' }, config, errors);
    const wastePercent = readNumber(input, { field: 'wastePercent', defaultValue: 10, range: 'nonnegative' }, config, errors);
    const packagePrice = readNumber(input, { field: 'packagePrice', defaultValue: 0, range: 'nonnegative' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const grossNeedM2 = netNeedM2 * (1 + wastePercent / 100);
    const packageCount = exactPurchaseCount(exactQuantityWithWaste(exactDecimal(netNeedM2), wastePercent), exactDecimal(packageAreaM2));
    const purchasedAreaM2 = packageCount * packageAreaM2;
    const excessAreaM2 = Math.max(0, purchasedAreaM2 - grossNeedM2);
    const cost = estimatedCost(packageCount, packagePrice);
    return finiteResult({
        toolId: 'kutu-paket-sayisi', netNeedM2, wastePercent, grossNeedM2, packageAreaM2,
        packageCount, purchasedAreaM2, excessAreaM2, packagePrice, estimatedCost: cost,
    }, [grossNeedM2, packageCount, purchasedAreaM2, excessAreaM2, cost], config.metadata, [grossNeedM2, packageCount, purchasedAreaM2, cost]);
}
function calculateLaminate(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const roomWidthM = readNumber(input, { field: 'roomWidthM', range: 'positive' }, config, errors);
    const roomLengthM = readNumber(input, { field: 'roomLengthM', range: 'positive' }, config, errors);
    const packageAreaM2 = readNumber(input, { field: 'packageAreaM2', range: 'positive' }, config, errors);
    const wastePercent = readNumber(input, { field: 'wastePercent', defaultValue: 10, range: 'nonnegative' }, config, errors);
    const parquetPricePerM2 = readNumber(input, { field: 'parquetPricePerM2', defaultValue: 0, range: 'nonnegative' }, config, errors);
    const includeSkirting = readBoolean(input, 'includeSkirting', true, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const netAreaM2 = roomWidthM * roomLengthM;
    const grossNeedM2 = netAreaM2 * (1 + wastePercent / 100);
    const exactNetAreaM2 = multiplyExactDecimals(exactDecimal(roomWidthM), exactDecimal(roomLengthM));
    const packageCount = exactPurchaseCount(exactQuantityWithWaste(exactNetAreaM2, wastePercent), exactDecimal(packageAreaM2));
    const purchasedAreaM2 = packageCount * packageAreaM2;
    const excessAreaM2 = Math.max(0, purchasedAreaM2 - grossNeedM2);
    const skirtingLengthM = includeSkirting ? 2 * (roomWidthM + roomLengthM) : 0;
    const cost = estimatedCost(purchasedAreaM2, parquetPricePerM2);
    return finiteResult({
        toolId: 'laminat-parke', netAreaM2, wastePercent, grossNeedM2, packageAreaM2, packageCount,
        purchasedAreaM2, excessAreaM2, includeSkirting, skirtingLengthM, parquetPricePerM2,
        estimatedCost: cost,
    }, [netAreaM2, grossNeedM2, packageCount, purchasedAreaM2, excessAreaM2, skirtingLengthM, cost], config.metadata, [
        netAreaM2, grossNeedM2, packageCount, purchasedAreaM2,
        ...(includeSkirting ? [skirtingLengthM] : []),
        cost,
    ]);
}
function calculateRoomArea(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const roomWidthM = readNumber(input, { field: 'roomWidthM', range: 'positive' }, config, errors);
    const roomLengthM = readNumber(input, { field: 'roomLengthM', range: 'positive' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const areaM2 = roomWidthM * roomLengthM;
    const perimeterM = 2 * (roomWidthM + roomLengthM);
    const diagonalM = Math.sqrt(roomWidthM * roomWidthM + roomLengthM * roomLengthM);
    return finiteResult({ toolId: 'oda-metrekare', areaM2, perimeterM, diagonalM }, [areaM2, perimeterM, diagonalM], config.metadata, [areaM2, perimeterM, diagonalM]);
}
function calculateSkirting(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const roomWidthM = readNumber(input, { field: 'roomWidthM', range: 'positive' }, config, errors);
    const roomLengthM = readNumber(input, { field: 'roomLengthM', range: 'positive' }, config, errors);
    const doorCount = readNumber(input, { field: 'doorCount', defaultValue: 1, range: 'nonnegative', integer: true }, config, errors);
    const doorWidthCm = readNumber(input, { field: 'doorWidthCm', defaultValue: 90, range: 'nonnegative' }, config, errors);
    const wastePercent = readNumber(input, { field: 'wastePercent', defaultValue: 10, range: 'nonnegative' }, config, errors);
    const pieceLengthM = readNumber(input, { field: 'pieceLengthM', defaultValue: 2.4, range: 'positive' }, config, errors);
    const piecePrice = readNumber(input, { field: 'piecePrice', defaultValue: 0, range: 'nonnegative' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const perimeterM = 2 * (roomWidthM + roomLengthM);
    const doorOpeningM = doorCount * (doorWidthCm / 100);
    const exactNetLengthM = exactSkirtingPerimeterMinusDoors(roomWidthM, roomLengthM, doorCount, doorWidthCm);
    const skirtingLengthOrder = exactDecimalSign(exactNetLengthM);
    if (skirtingLengthOrder < 0) {
        return failure([{
                field: 'doorWidthCm',
                code: 'OUT_OF_RANGE',
                message: 'Kapı sayısı ile kapı eninin toplamı oda çevresini aşamaz.',
            }], config.metadata);
    }
    const netLengthM = exactDecimalToNumber(exactNetLengthM);
    const grossLengthM = netLengthM * (1 + wastePercent / 100);
    const pieceCount = exactPurchaseCount(exactQuantityWithWaste(exactNetLengthM, wastePercent), exactDecimal(pieceLengthM));
    const purchasedLengthM = pieceCount * pieceLengthM;
    const cost = estimatedCost(pieceCount, piecePrice);
    return finiteResult({
        toolId: 'supurgelik-metre', perimeterM, doorOpeningM, netLengthM, wastePercent,
        grossLengthM, pieceLengthM, pieceCount, purchasedLengthM, piecePrice, estimatedCost: cost,
    }, [perimeterM, doorOpeningM, netLengthM, grossLengthM, pieceCount, purchasedLengthM, cost], config.metadata, [
        perimeterM,
        ...(doorCount > 0 && doorWidthCm > 0 ? [doorOpeningM] : []),
        ...(skirtingLengthOrder > 0
            ? [netLengthM, grossLengthM, pieceCount, purchasedLengthM, cost]
            : []),
    ]);
}
function calculateZebraBlind(input: Readonly<Record<string, unknown>>, config: Core.BaseConfig) {
    const errors: Core.InputError[] = [];
    const windowWidthCm = readNumber(input, { field: 'windowWidthCm', range: 'positive' }, config, errors);
    const windowHeightCm = readNumber(input, { field: 'windowHeightCm', range: 'positive' }, config, errors);
    const mechanismAllowanceCm = readNumber(input, { field: 'mechanismAllowanceCm', defaultValue: 4, range: 'nonnegative' }, config, errors);
    const minimumBillableAreaM2 = readNumber(input, { field: 'minimumBillableAreaM2', defaultValue: 1, range: 'positive' }, config, errors);
    const pricePerM2 = readNumber(input, { field: 'pricePerM2', defaultValue: 0, range: 'nonnegative' }, config, errors);
    if (errors.length > 0)
        return failure(errors, config.metadata);
    const orderWidthM = (windowWidthCm + mechanismAllowanceCm) / 100;
    const orderHeightM = windowHeightCm / 100;
    const actualAreaM2 = orderWidthM * orderHeightM;
    const billableAreaM2 = Math.max(actualAreaM2, minimumBillableAreaM2);
    const cost = estimatedCost(billableAreaM2, pricePerM2);
    return finiteResult({
        toolId: 'zebra-stor-perde-olcu', orderWidthM, orderHeightM, actualAreaM2,
        minimumBillableAreaM2, billableAreaM2, pricePerM2, estimatedCost: cost,
    }, [orderWidthM, orderHeightM, actualAreaM2, billableAreaM2, cost], config.metadata, [orderWidthM, orderHeightM, actualAreaM2, billableAreaM2, cost]);
}
export function calculateHomeRenovation(input: Core.HomeRenovationInput, config: Core.BaseConfig | undefined) {
    if (!input || typeof input !== 'object') {
        const fallback = DEFAULT_HOME_RENOVATION_CONFIGS['alan-fire'].metadata;
        return failure([{ field: 'input', code: 'REQUIRED', message: 'Ev-tadilat hesap girdileri gereklidir.' }], fallback);
    }
    const record = input;
    const rawToolId = record.toolId;
    if (!isToolId(rawToolId)) {
        const fallback = DEFAULT_HOME_RENOVATION_CONFIGS['alan-fire'].metadata;
        return failure([{ field: 'toolId', code: 'UNSUPPORTED_VALUE', message: 'Ev-tadilat araç kimliği desteklenmiyor.' }], fallback);
    }
    const fallbackConfig = DEFAULT_HOME_RENOVATION_CONFIGS[rawToolId];
    const candidate = config === undefined ? fallbackConfig : config;
    const metadata = resolveMetadata(candidate?.metadata, fallbackConfig.metadata);
    if (!candidate || typeof candidate !== 'object') {
        return failure([{ field: 'config', code: 'INVALID_CONFIG', message: 'Ev-tadilat konfigürasyonu geçersiz.' }], metadata);
    }
    const configErrors = validateConfig(candidate, rawToolId);
    if (configErrors.length > 0)
        return failure(configErrors, metadata);
    const resolvedConfig = { ...candidate, metadata };
    switch (rawToolId) {
        case 'alan-fire': return calculateAreaFire(record, resolvedConfig);
        case 'alci-siva-macun': return calculatePlaster(record, resolvedConfig);
        case 'boya': return calculatePaint(record, resolvedConfig);
        case 'derz-dolgu': return calculateGrout(record, resolvedConfig);
        case 'duvar-kagidi-rulo': return calculateWallpaper(record, resolvedConfig);
        case 'fayans-seramik': return calculateTile(record, resolvedConfig);
        case 'curtain-fabric': return calculateCurtainFabric(record, resolvedConfig);
        case 'hali-metrekare': return calculateCarpet(record, resolvedConfig);
        case 'kutu-paket-sayisi': return calculatePackageCount(record, resolvedConfig);
        case 'laminat-parke': return calculateLaminate(record, resolvedConfig);
        case 'oda-metrekare': return calculateRoomArea(record, resolvedConfig);
        case 'supurgelik-metre': return calculateSkirting(record, resolvedConfig);
        case 'zebra-stor-perde-olcu': return calculateZebraBlind(record, resolvedConfig);
    }
}
